import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthState } from '@/components/feature/AuthGuard';
import OgShell from '../components/OgShell';
import { QiAuthForm } from '../components/AuthGate';
import {
  EMPTY_QI_INTAKE, intakePractitioners, loadMyEditableIntake, saveMyIntake,
  type QiIntakeForm,
} from '../lib/intakeApi';
import type { VerifiedPractitioner } from '../lib/api';

const sections: Array<{ key: keyof QiIntakeForm; label: string; placeholder?: string }> = [
  { key: 'concerns_body', label: '身體狀況' },
  { key: 'concerns_emotion', label: '情緒狀況' },
  { key: 'concerns_life', label: '生活狀況' },
  { key: 'concerns_work', label: '工作狀況' },
];

export default function QiIntakePage() {
  const auth = useAuthState();
  const [id, setId] = useState<number | null>(null);
  const [form, setForm] = useState<QiIntakeForm>(EMPTY_QI_INTAKE);
  const [providers, setProviders] = useState<VerifiedPractitioner[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!auth.user) return;
    Promise.all([loadMyEditableIntake(), intakePractitioners()])
      .then(([draft, people]) => {
        setProviders(people);
        if (draft) {
          setId(draft.id);
          setForm(Object.fromEntries(
            Object.keys(EMPTY_QI_INTAKE).map((key) => [
              key, String(draft[key as keyof QiIntakeForm] ?? ''),
            ]),
          ) as unknown as QiIntakeForm);
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : '載入失敗'));
  }, [auth.user]);

  const update = (key: keyof QiIntakeForm, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const persist = async (submit: boolean) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const row = await saveMyIntake(id, form, submit);
      setId(row.id);
      setNotice(submit
        ? '申請已送出，現在進入人工審核；審核通過後才會交由所選服務者接受。'
        : '草稿已儲存，可稍後回來繼續。');
    } catch (e) {
      setError(e instanceof Error ? e.message : '儲存失敗');
    } finally { setBusy(false); }
  };

  if (!auth.user) return (
    <OgShell><div className="max-w-xl mx-auto px-4 py-10 space-y-5">
      <h1 className="text-2xl text-foreground-900">建立我的療育申請</h1>
      <p className="text-sm text-foreground-500">請先登入 AUM1001，資料將以私人方式保存。</p>
      <QiAuthForm />
    </div></OgShell>
  );

  return <OgShell><div className="max-w-3xl mx-auto px-4 md:px-6 py-8 space-y-6">
    <header className="space-y-2">
      <Link to="/og/qi-healing" className="text-sm text-accent-600">← 回宇炁療育登錄</Link>
      <h1 className="text-2xl text-foreground-900">建立我的療育申請</h1>
      <p className="text-sm text-foreground-500 leading-relaxed">
        先由本人建立資料並選擇服務者。送出後依序進入人工審核與服務者接受；尚未接受前不會建立正式療育案例。
      </p>
    </header>

    <section className="bg-background-100 border border-foreground-200/50 rounded-xl p-5 space-y-4">
      <h2 className="font-medium text-foreground-800">一、本人與緊急聯絡資料</h2>
      <div className="grid md:grid-cols-2 gap-3">
        <Input label="真實姓名＊" value={form.applicant_name} onChange={(v) => update('applicant_name', v)} />
        <Input label="電話＊" value={form.phone} onChange={(v) => update('phone', v)} />
        <Input label="出生日期" type="date" value={form.birth_date} onChange={(v) => update('birth_date', v)} />
        <Input label="居住都市＊" value={form.residence_city} onChange={(v) => update('residence_city', v)} />
        <Input label="聯絡地址" value={form.address} onChange={(v) => update('address', v)} />
        <Input label="緊急聯絡人" value={form.emergency_contact_name} onChange={(v) => update('emergency_contact_name', v)} />
        <Input label="緊急聯絡人電話" value={form.emergency_contact_phone} onChange={(v) => update('emergency_contact_phone', v)} />
      </div>
    </section>

    <section className="bg-background-100 border border-foreground-200/50 rounded-xl p-5 space-y-4">
      <h2 className="font-medium text-foreground-800">二、現況、問題與目標</h2>
      <div className="grid md:grid-cols-2 gap-3">
        {sections.map((item) => <Area key={item.key} label={item.label}
          value={form[item.key]} onChange={(v) => update(item.key, v)} />)}
      </div>
      <Area label="目前最想處理的問題＊" value={form.primary_problem} onChange={(v) => update('primary_problem', v)} />
      <Area label="希望達成的目標＊" value={form.goals} onChange={(v) => update('goals', v)} />
      <Area label="問題起始、持續時間與重要歷程" value={form.history} onChange={(v) => update('history', v)} />
    </section>

    <section className="bg-background-100 border border-foreground-200/50 rounded-xl p-5 space-y-4">
      <h2 className="font-medium text-foreground-800">三、健康安全資料</h2>
      <Area label="疾病／就醫／手術史" value={form.medical_history} onChange={(v) => update('medical_history', v)} />
      <Area label="目前藥物與治療" value={form.medications} onChange={(v) => update('medications', v)} />
      <Area label="過敏與其他注意事項" value={form.allergies} onChange={(v) => update('allergies', v)} />
      <p className="text-xs text-foreground-500">本系統不取代醫療診斷或治療，也不會要求停止既有醫療或藥物。</p>
    </section>

    <section className="bg-background-100 border border-foreground-200/50 rounded-xl p-5 space-y-4">
      <h2 className="font-medium text-foreground-800">四、選擇服務者、方式與都市</h2>
      <div className="grid md:grid-cols-2 gap-3">
        <Select label="服務階段＊" value={form.service_stage} onChange={(v) => update('service_stage', v)} options={[
          ['first_request', '首次申請'], ['continuing_service', '持續服務'], ['unsure', '不確定'],
        ]} />
        <Select label="希望方式＊" value={form.service_mode} onChange={(v) => update('service_mode', v)} options={[
          ['either', '線上或實體皆可'], ['online', '線上'], ['in_person', '實體'],
        ]} />
      </div>
      <Input label="希望接受服務的都市＊" value={form.preferred_service_city} onChange={(v) => update('preferred_service_city', v)} />
      <p className="text-xs text-foreground-500">本系統使用「首次申請／持續服務」，不使用「初診／複診」；實際安排仍以服務者確認為準。</p>
      <label className="block text-sm text-foreground-700 space-y-1">
        <span>正式授權服務者＊</span>
        <select value={form.practitioner_id} onChange={(e) => update('practitioner_id', e.target.value)}
          className="w-full rounded-md border border-foreground-200 bg-background-50 px-3 py-2.5">
          <option value="">請選擇</option>
          {providers.map((p) => <option key={p.user_id} value={p.user_id}>{p.legal_name || '正式授權服務者'}</option>)}
        </select>
      </label>
      <Area label="希望聯絡／服務時段" value={form.preferred_times} onChange={(v) => update('preferred_times', v)} />
      <Area label="想先讓服務者知道的其他事項" value={form.applicant_notes} onChange={(v) => update('applicant_notes', v)} />
    </section>

    <section className="rounded-xl border border-accent-200 bg-accent-50/60 p-4 text-xs text-foreground-600 leading-relaxed">
      送出即表示本人確認資料屬實，並同意指定審核人員與所選服務者依權限查看本申請。公開見證、媒體使用與 7／30／90 日追蹤將另行取得同意；本次送出不等於公開授權。
    </section>

    {error && <p className="rounded-md bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>}
    {notice && <p className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{notice}</p>}

    <div className="flex flex-wrap justify-end gap-3">
      <button disabled={busy} onClick={() => persist(false)}
        className="rounded-md border border-foreground-200 px-5 py-2.5 text-sm">儲存草稿</button>
      <button disabled={busy} onClick={() => persist(true)}
        className="rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50">
        {busy ? '處理中…' : '確認同意並送出審核'}
      </button>
    </div>
  </div></OgShell>;
}

function Input({ label, value, onChange, type = 'text' }: {
  label: string; value: string; onChange: (value: string) => void; type?: string;
}) {
  return <label className="block text-sm text-foreground-700 space-y-1"><span>{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-foreground-200 bg-background-50 px-3 py-2.5" />
  </label>;
}

function Select({ label, value, onChange, options }: {
  label: string; value: string; onChange: (value: string) => void; options: Array<[string, string]>;
}) {
  return <label className="block text-sm text-foreground-700 space-y-1"><span>{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-foreground-200 bg-background-50 px-3 py-2.5">
      {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
    </select>
  </label>;
}

function Area({ label, value, onChange }: {
  label: string; value: string; onChange: (value: string) => void;
}) {
  return <label className="block text-sm text-foreground-700 space-y-1"><span>{label}</span>
    <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full resize-y rounded-md border border-foreground-200 bg-background-50 px-3 py-2.5" />
  </label>;
}
